import { useMemo, useState } from "react";
import axios from "axios";
import { format as fmt } from "date-fns";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2, Globe, Building2, Mountain } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FORMATS, TIME_SLOTS, TIMEZONES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { online: Globe, "harley-street": Building2, andalusia: Mountain };

const Step = ({ n, title, children }) => (
  <div className="border-t border-[#C5A059]/20 pt-8">
    <div className="flex items-baseline gap-4 mb-6">
      <span className="font-data text-xs text-[#C5A059]">{n}</span>
      <h3 className="font-display text-2xl sm:text-3xl text-slate-50">{title}</h3>
    </div>
    {children}
  </div>
);

const inputCls = "w-full bg-[#0B1120] border border-white/15 focus:border-[#C5A059] outline-none px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 transition-colors duration-300";

const detectTz = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return TIMEZONES.some((t) => t.id === tz) ? tz : "Europe/London";
};

export default function ConsultationForm({ defaultUrgent = false, defaultFormat = "online" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", format: defaultFormat, urgent: defaultUrgent });
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [tz, setTz] = useState(detectTz);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot) return toast.error("Please choose a date and time slot.");
    if (form.name.trim().length < 2 || !form.email.includes("@")) return toast.error("Please add your name and a valid email.");
    setSending(true);
    try {
      const payload = { ...form, date: fmt(date, "yyyy-MM-dd"), time_slot: slot, timezone: tz };
      await axios.post(`${API}/consultations`, payload);
      setDone(payload);
      toast.success("Request received. We will be in touch confidentially.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    const f = FORMATS.find((x) => x.id === done.format);
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="consultation-success" className="bg-[#131B2E] border border-[#C5A059]/40 p-10 sm:p-14">
        <span className="w-14 h-14 grid place-items-center border border-[#C5A059] text-[#C5A059]"><Check className="w-6 h-6" /></span>
        <h3 className="font-display text-4xl sm:text-5xl text-slate-50 mt-8">Thank you, {done.name.split(" ")[0]}.</h3>
        <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">Your request has been received in confidence. A member of our team will contact you to confirm your consultation.</p>
        <dl className="mt-10 grid sm:grid-cols-3 gap-6 border-t border-white/10 pt-8 text-sm">
          <div><dt className="eyebrow mb-2">Format</dt><dd className="text-slate-100" data-testid="success-format">{f?.label}</dd></div>
          <div><dt className="eyebrow mb-2">Date</dt><dd className="text-slate-100" data-testid="success-date">{fmt(date, "EEEE d MMMM yyyy")}</dd></div>
          <div><dt className="eyebrow mb-2">Time</dt><dd className="text-slate-100" data-testid="success-time">{done.time_slot} · {TIMEZONES.find((t) => t.id === done.timezone)?.label}</dd></div>
        </dl>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="consultation-form" className="space-y-12">
      <Step n="01" title="Choose your format">
        <div className="grid sm:grid-cols-3 gap-3">
          {FORMATS.map((f) => {
            const Icon = ICONS[f.id];
            const on = form.format === f.id;
            return (
              <button type="button" key={f.id} onClick={() => set("format")(f.id)} data-testid={`consultation-format-${f.id}`}
                className={`text-left p-5 border transition-[border-color,background-color] duration-300 ${on ? "border-[#C5A059] bg-[#131B2E]" : "border-white/10 hover:border-white/30"}`}>
                <Icon className={`w-5 h-5 ${on ? "text-[#C5A059]" : "text-slate-400"}`} />
                <span className="block mt-4 text-slate-100 font-medium text-sm">{f.label}</span>
                <span className="block mt-1 text-xs text-slate-400">{f.detail}</span>
              </button>
            );
          })}
        </div>
      </Step>

      <Step n="02" title="Schedule your consultation">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-[#0B1120] border border-white/10 p-3 flex justify-center" data-testid="consultation-form-date-picker">
            <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < today} className="text-slate-100" />
          </div>
          <div className="space-y-6">
            <div>
              <label className="eyebrow block mb-3">Time zone</label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger data-testid="consultation-form-timezone-select" className="rounded-none h-12 bg-[#0B1120] border-white/15 text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B1120] border-[#C5A059]/30 text-slate-100">
                  {TIMEZONES.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`timezone-option-${t.id.replace("/", "-").toLowerCase()}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="eyebrow block mb-3">Time slot {date && <span className="text-slate-400 normal-case tracking-normal font-sans">· {fmt(date, "EEE d MMM")}</span>}</label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="consultation-form-time-slot-select">
                {TIME_SLOTS.map((t) => (
                  <button type="button" key={t} onClick={() => setSlot(t)} data-testid={`time-slot-${t.replace(":", "")}`}
                    className={`py-3 font-data text-xs border transition-colors duration-300 ${slot === t ? "bg-[#C5A059] text-[#070A12] border-[#C5A059]" : "border-white/15 text-slate-200 hover:border-[#C5A059]/60"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Step>

      <Step n="03" title="Your private details">
        <div className="grid sm:grid-cols-2 gap-4">
          <input className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} data-testid="consultation-form-name-input" />
          <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="consultation-form-email-input" />
          <input className={`${inputCls} sm:col-span-2`} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="consultation-form-phone-input" />
          <textarea className={`${inputCls} sm:col-span-2 min-h-[130px] resize-y`} placeholder="Anything you would like us to know (optional)" value={form.message} onChange={set("message")} data-testid="consultation-form-message-input" />
        </div>
        <label className="mt-5 flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
          <Checkbox checked={form.urgent} onCheckedChange={(v) => set("urgent")(!!v)} data-testid="consultation-form-urgent-checkbox" className="mt-0.5 rounded-none border-red-400/60 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500" />
          <span>This relates to a recent incident and I need <span className="text-red-300">immediate post-incident support</span>.</span>
        </label>
      </Step>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-t border-[#C5A059]/20 pt-8">
        <p className="text-xs text-slate-400 max-w-sm">Your information is handled in strict confidence and used only to arrange your consultation.</p>
        <button type="submit" disabled={sending} data-testid="consultation-form-submit-button" className="btn-bronze group inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-medium tracking-wide disabled:opacity-60">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Request Confidential Consultation
          <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
        </button>
      </div>
    </form>
  );
}
